import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { verifyEmail } from "../../store/slices/emailVerification/asyncThunks";
import {
  selectVerifyEmailState,
  clearVerifyState,
} from "../../store/slices/emailVerification/slice";
import { FiCheck, FiX, FiLoader } from "react-icons/fi";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 20px;
  padding: 3rem;
  max-width: 500px;
  overflow: hidden;
  width: 100%;
  border: 2px solid rgba(255, 107, 53, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  ${css`
    animation: ${fadeIn} 0.6s ease-out;
  `}
  text-align: center;
  backdrop-filter: blur(15px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #ff8c42);
    border-radius: 20px 20px 0 0;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2.5rem;

  ${({ $status }) => {
    if ($status === "loading") {
      return css`
        background: rgba(255, 107, 53, 0.2);
        color: #ff6b35;
        svg {
          animation: ${spin} 1s linear infinite;
        }
      `;
    } else if ($status === "success") {
      return css`
        background: rgba(46, 160, 67, 0.2);
        color: #4caf50;
      `;
    } else {
      return css`
        background: rgba(244, 67, 54, 0.2);
        color: #f44336;
      `;
    }
  }}
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Message = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  color: #ffffff;
  border: 1px solid rgba(255, 107, 53, 0.5);
  padding: 12px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(135deg, #ff8c42, #ffab73);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  }
`;

const EmailVerificationPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const verifyState = useSelector(selectVerifyEmailState);

  useEffect(() => {
    dispatch(clearVerifyState());

    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) {
      return;
    }

    dispatch(verifyEmail(token));
  }, [location, dispatch]);

  const handleGoToDashboard = () => {
    navigate("/bend-panel/kontrolna-tabla");
  };

  const getIcon = () => {
    if (verifyState.loading) return <FiLoader />;
    if (verifyState.success) return <FiCheck />;
    return <FiX />;
  };

  const getTitle = () => {
    if (verifyState.loading) return "Verifikacija u toku...";
    if (verifyState.success) return "Verifikacija uspešna!";
    return "Verifikacija neuspešna";
  };

  const getMessage = () => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) {
      return "Nedostaje verifikacioni token.";
    }

    if (verifyState.loading) {
      return "Molimo sačekajte...";
    }

    if (verifyState.success) {
      return "Vaša email adresa je uspešno verifikovana!";
    }

    if (verifyState.error) {
      return verifyState.error;
    }

    return "Greška pri verifikaciji email adrese.";
  };

  const getStatus = () => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) return "error";
    if (verifyState.loading) return "loading";
    if (verifyState.success) return "success";
    return "error";
  };

  return (
    <Container>
      <Card>
        <IconContainer $status={getStatus()}>{getIcon()}</IconContainer>

        <Title>{getTitle()}</Title>

        <Message>{getMessage()}</Message>

        {verifyState.user && (
          <Message>
            Dobro došli, <strong>@{verifyState.user.username}</strong>
            {verifyState.user.bandName && ` (${verifyState.user.bandName})`}!
          </Message>
        )}

        {verifyState.success && (
          <Button onClick={handleGoToDashboard}>Idite na Dashboard</Button>
        )}
      </Card>
    </Container>
  );
};

export default EmailVerificationPage;
